/**
 * D-020 LT2-017 — the nearest-three multi-line expanded view.
 *
 * When `spreadTotalNearestLines` is on, expanding a spread/total row renders
 * the SELECTED line plus its two nearest neighbors as three adjacent
 * two-sided ladders — ascending line order, the selected (center-of-window)
 * line visually emphasized, each block headed by its own line label +
 * availability badge. Blocks for unavailable lines render but stay inert
 * (TwoSidedLadderBody gates clicks on availability), so a dead line can
 * never consume an order.
 *
 * Every level click carries the BLOCK's own line index via
 * `PillTradeMeta.lineIdx`, so the pill-trade handlers synthesize the clicked
 * line's market — not the selected one (LT2-006 stable identity across the
 * window). Hotkey focus stays on the CARD: A/D keep resolving the selected
 * line only (documented in the EventRows resolver).
 */
import { useMemo } from 'react';
import { useMarketStore } from '@/stores/marketStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { fairPctText } from '@/lib/opOdds';
import { formatCompactCurrency } from '@/lib/infra/formatters';
import { formatVolume } from '../ValueDashboard/ladder/helpers';
import { butterflyCardContext, type ButterflyEventIdentity } from './butterflyContext';
import { cn } from '@/lib/infra/utils';
import { Td } from '@/components/atoms/TableCells';
import type { SpreadLine, TotalLine } from '../ValueDashboard/types';
import type { UserOrderSlice } from '@/hooks/useUserKalshiPOS';
import {
  classifyLineAvailability,
  LINE_AVAILABILITY_LABEL,
} from '@/lib/sport/lineAvailability';
import { formatSignedLine, nearestLineWindow } from './synthesize';
import { BUTTERFLY_NARROW_LADDER, TwoSidedLadderBody } from './TwoSidedLadder';
import { useLayoutEffect, useRef, useState } from 'react';

/**
 * #884 — whether the three-card row is wider than its scroller. The row is
 * `overflow-x-auto`; at 1470×956 three narrow cards do not fit beside the
 * rail, and a scrollbar the platform may not paint is not a signal. When the
 * row overflows, a visible caption says so; nothing else moves.
 */
function useRowOverflow(): [React.RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  });
  return [ref, overflowing];
}

export interface NearestLinesPanelProps {
  event: ButterflyEventIdentity;
  period?: string;
  kind: 'spread' | 'total';
  lines: SpreadLine[] | TotalLine[];
  selectedIdx: number;
  yesLabel: string; // 'YES' | 'OVER'
  noLabel: string; // 'NO' | 'UNDER'
  makerRebatePct: number;
  /** Resolve the trader's open orders for one line's Kalshi ticker. */
  userOrdersFor: (ticker: string) => UserOrderSlice[] | null;
  /** Level click, tagged with the block's own line index. */
  onLevelClick: (
    lineIdx: number,
    side: 'yes' | 'no',
    levelKind: 'bid' | 'ask',
    cents: number,
  ) => void;
  colSpan?: number;
  focused?: boolean;
  /** #609 — the focused card's id, so the expanded slot can carry the hotkey
   *  fence's marks. This row is where the submittable price is DISPLAYED once
   *  the card is expanded (the resolver reads the join bid from here, not from
   *  the action row's pills), so the fence tests THIS row's visibility. Null
   *  whenever hotkeys are off — the marks must never appear then. */
  lt2FocusId?: string | null;
  /** LT2-018 — NO/UNDER columns render left in every block. */
  flipColumns?: boolean;
}

function sourceTime(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms) || Math.abs(ms) > 8.64e15) return 'Source time unavailable';
  return `As of ${new Date(ms).toISOString().slice(0, 23).replace('T', ' ')} UTC`;
}

function lineLabel(kind: 'spread' | 'total', line: SpreadLine | TotalLine): string {
  return kind === 'spread'
    ? formatSignedLine((line as SpreadLine).line)
    : String((line as TotalLine).line);
}

export function NearestLinesPanel({
  event,
  period = 'full',
  kind,
  lines,
  selectedIdx,
  yesLabel,
  noLabel,
  makerRebatePct,
  userOrdersFor,
  onLevelClick,
  colSpan = 12,
  focused = false,
  lt2FocusId = null,
  flipColumns = false,
}: NearestLinesPanelProps) {
  const version = useMarketStore((s) => s.version);
  const spreadReader = useMarketStore((s) => s.getOpSpreadLineFor);
  const marketsReader = useMarketStore((s) => s.getOpMarketsFor);
  const totalReader = useMarketStore((s) => s.getOpTotalLineFor);
  const showReference = useDisplaySettings((s) => s.showOpOdds);
  const window = useMemo(() => nearestLineWindow(lines.length, selectedIdx), [lines.length, selectedIdx]);
  const contexts = useMemo(() => {
    void version;
    return new Map(window.map(index => [index, butterflyCardContext(event, kind, lines[index],
      { spread: spreadReader, total: totalReader, markets: marketsReader }, period)]));
  }, [event, kind, lines, period, version, spreadReader, totalReader, marketsReader, window]);
  const marks = focused && lt2FocusId ? lt2FocusId : undefined;
  const [rowRef, rowOverflows] = useRowOverflow();
  return (
    <tr
      className={cn('border-0', focused && '!bg-primary/15')}
      data-testid="nearest-lines-panel"
      data-lt2-focused={marks}
      data-lt2-price={marks == null ? undefined : ''}
      data-lt2-hotkey-control={marks == null ? undefined : ''}
    >
      <Td colSpan={colSpan} className="py-1.5">
        {rowOverflows && (
          <div
            data-testid="butterfly-scroll-signal"
            className="text-muted-foreground mb-1 px-2 text-[10px] leading-4"
          >
            Scroll to view all three lines.
          </div>
        )}
        <div
          ref={rowRef}
          data-testid="butterfly-row"
          data-overflowing={rowOverflows ? '' : undefined}
          // `w-0 min-w-full`: the row contributes no intrinsic width to its
          // table cell, so three cards cannot widen the table (which at
          // 1470 would clip the third card behind the table's own scroller
          // with nothing saying so); they overflow THIS row, which scrolls
          // and is signaled above.
          className="flex w-0 min-w-full items-stretch gap-2 overflow-x-auto px-2 [scrollbar-gutter:stable]"
        >
          {window.map((idx) => {
            const line = lines[idx];
            if (!line) return null;
            const context = contexts.get(idx)!;
            const kalshi = context.book;
            const availability = classifyLineAvailability(kalshi);
            const isSelected = idx === selectedIdx;
            return (
              <div
                key={context.key}
                data-butterfly-market={context.ticker ?? "absent"}
                data-butterfly-identity={context.key}
                data-testid={`nearest-line-block-${idx}`}
                className={cn(
                  // The narrow book's own width plus card padding: the card
                  // never squeezes its rungs (#884 blocker), it overflows the
                  // row instead, which is signaled above.
                  'min-w-[30rem] flex-1 rounded border p-1.5',
                  isSelected ? 'border-primary/50 bg-primary/5' : 'border-border/40',
                )}
              >
                <div className="mb-1 flex h-6 items-center gap-1.5">
                  <span
                    className={cn(
                      'font-mono text-xs font-semibold',
                      isSelected ? 'text-primary' : 'text-foreground',
                    )}
                  >
                    {kind === 'spread' && flipColumns ? formatSignedLine(-line.line) : lineLabel(kind, line)}
                  </span>
                  {isSelected && (
                    <span className="text-muted-foreground text-[9px] uppercase tracking-wide">
                      selected
                    </span>
                  )}
                  {availability !== 'active' && (
                    <span
                      className="rounded border border-amber-500/40 bg-amber-500/10 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-500"
                      title="Order entry disabled on this line"
                    >
                      {LINE_AVAILABILITY_LABEL[availability]}
                    </span>
                  )}
                </div>
                <div className="mb-1 space-y-0.5 text-xs leading-4" data-butterfly-context="">
                  <div className="font-semibold">YES {context.yesLabel} · NO {context.noLabel}</div>
                  {/* The badge above already names a non-active state; only
                      "active" has nowhere else to show. */}
                  <div className="text-muted-foreground">
                    Kalshi · {period === 'full' ? 'Full game' : period}
                    {context.availability === 'active' && ' · active'}
                  </div>
                  <div className="flex flex-wrap gap-x-3 font-mono tabular-nums">
                    <span>{context.volumeKind}: {context.volume == null ? '—' : formatVolume(context.volume)}</span>
                    <span>Resting $: {context.liquidity == null ? '—' : formatCompactCurrency(context.liquidity)}</span>
                  </div>
                  {/* #884 — the reference block is five constant-height blocks (six visual lines)
                      (values, Pinnacle time, source, YES time, NO time) so a routine update —
                      a second timestamp appearing, a withdrawal or push note
                      arriving, a source changing — never changes its height
                      and never moves the executable prices under it. The
                      source line stays one line; its flags come first so a
                      long anchor name is what gives way, and the full text
                      rides the title. Both times are always printed. */}
                  {showReference && (() => {
                    const sourceText =
                      context.reference.yes.source == null && context.reference.no.source == null
                        ? 'Exact reference unavailable'
                        : [
                            context.reference.pinnacleWithdrawn ? 'Pinnacle withdrawn' : null,
                            context.reference.canPush ? 'Can push' : null,
                            [...new Set([context.reference.yes.source, context.reference.no.source].filter(Boolean))].join(' / '),
                          ]
                            .filter(Boolean)
                            .join(' · ');
                    return (
                      <div className="rounded border border-border/40 px-1 py-0.5" data-butterfly-reference="">
                        <div className="flex justify-between gap-2 font-mono tabular-nums">
                          <span>YES {fairPctText(context.reference.yes.tenths) == null ? '—' : `${fairPctText(context.reference.yes.tenths)}%`}</span>
                          <span>NO {fairPctText(context.reference.no.tenths) == null ? '—' : `${fairPctText(context.reference.no.tenths)}%`}</span>
                        </div>
                        <div className="font-mono tabular-nums whitespace-nowrap" data-butterfly-pinnacle-time="">Pinnacle {sourceTime(context.fairView?.anchorUpdatedAtMs ?? null)}</div>
                        <div className="text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap" title={sourceText} data-butterfly-source="">
                          {sourceText}
                        </div>
                        <div className="text-muted-foreground font-mono tabular-nums whitespace-nowrap" data-butterfly-time="yes">
                          YES {sourceTime(context.reference.yes.updatedAtMs)}
                        </div>
                        <div className="text-muted-foreground font-mono tabular-nums whitespace-nowrap" data-butterfly-time="no">
                          NO {sourceTime(context.reference.no.updatedAtMs)}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <TwoSidedLadderBody
                  kalshi={kalshi}
                  yesLabel={yesLabel}
                  noLabel={noLabel}
                  userOrders={kalshi ? userOrdersFor(kalshi.marketTicker) : null}
                  availability={availability}
                  makerRebatePct={makerRebatePct}
                  onLevelClick={(side, levelKind, cents) =>
                    onLevelClick(idx, side, levelKind, cents)
                  }
                  flipColumns={flipColumns}
                  presentation={BUTTERFLY_NARROW_LADDER}
                  reference={showReference ? { fair: context.fairView, yesIsAway: context.yesIsAway, strike: context.strike } : undefined}
                />
              </div>
            );
          })}
          {Array.from({ length: Math.max(0, 3 - window.length) }, (_, i) => (
            <div key={`missing-${i}`} data-testid="butterfly-missing-alternate" className="min-w-[30rem] flex-1 rounded border border-dashed border-border/40 p-2 text-xs text-muted-foreground">
              No additional Kalshi line
            </div>
          ))}
        </div>
      </Td>
    </tr>
  );
}
